import { PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { PendingAttachmentPicker } from '@components/Attachments'
import { DateTimePicker } from '@components/DateTimePicker'
import LocationAutocomplete from '@components/LocationAutocomplete'
import Modal from '@components/Modal'
import { useAuth } from '@context/AuthContext'
import { usePlaceSearchContext } from '@hooks'
import { addLinkRow, MAX_LINKS, removeLinkRow, setLinkRow } from '@lib/links'
import { fullMemberName, sortMembersWithCurrentFirst } from '@lib/members'
import { placeSecondaryText } from '@lib/place'
import { CreateAccommodationPayload } from '@hooks/useCreateAccommodation'
import { GetUserType } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    AccommodationDraft,
    emptyAccommodationDraft,
    toCreatePayload,
    validateAccommodationDraft,
    pickPlace,
    setCheckIn,
    setCheckOut,
    setLocationText,
    setName,
    setNotes,
    toggleGuest,
} from '../accommodationForm'
import {
    AddLinkButton,
    Button,
    ButtonGroup,
    CompactFormLabel,
    DateInputGroup,
    DateInputRow,
    FormDescription,
    FormGroup,
    FormInput,
    FormLabel,
    FormTextArea,
    GuestCheckbox,
    GuestError,
    GuestName,
    GuestRow,
    GuestSummary,
    GuestsList,
    LinkInput,
    LinkRow,
    LinksSection,
    LocationSecondaryText,
    RemoveLinkButton,
} from './AddAccommodationModal.styles'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'
import { TestIds } from '@/testIds'

export type AddAccommodationSubmitData = CreateAccommodationPayload & { pendingFiles?: File[] }

interface AddAccommodationModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: AddAccommodationSubmitData) => void
    members: GetUserType[]
    isSubmitting?: boolean
}

const AddAccommodationModal: React.FC<AddAccommodationModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    members,
    isSubmitting = false,
}) => {
    const { user } = useAuth()
    const { tripId } = useParams<{ tripId: string }>()

    const [draft, setDraft] = useState<AccommodationDraft>(() => emptyAccommodationDraft([]))
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    // A stay's search leans toward the destination its check-in falls in.
    const placeSearch = usePlaceSearchContext(tripId, { kind: 'accommodation', checkIn: draft.checkInDate })

    useEffect(() => {
        if (isOpen) {
            setDraft(emptyAccommodationDraft(members.map(m => m.id)))
            setPendingFiles([])
        }
    }, [isOpen, members])

    const validation = validateAccommodationDraft(draft)
    const canSubmit = validation.canSubmit && !isSubmitting

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSubmit) return

        onSubmit({
            ...toCreatePayload(draft),
            pendingFiles: pendingFiles.length > 0 ? pendingFiles : undefined,
        })
    }

    const sortedMembers = sortMembersWithCurrentFirst(members, user?.id)
    const locationSecondaryText = placeSecondaryText(draft.location)

    const formId = 'add-accommodation-form'

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Add Accommodation"
            size="large"
            backgroundColor={backgroundOrange}
            closeOnBackdropClick={false}
            footer={
                <ButtonGroup>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form={formId}
                        variant="primary"
                        disabled={!canSubmit}
                        data-testid={TestIds.accommodation.saveButton}
                    >
                        {isSubmitting ? 'Adding...' : 'Add Accommodation'}
                    </Button>
                </ButtonGroup>
            }
        >
            <form id={formId} onSubmit={handleSubmit}>
                <FormGroup>
                    <FormLabel htmlFor="accommodation-location">Location (Optional)</FormLabel>
                    <LocationAutocomplete
                        id="accommodation-location"
                        value={draft.location.text}
                        onChange={value => setDraft(d => setLocationText(d, value))}
                        onPlaceSelect={place => setDraft(d => pickPlace(d, place, { fillEmptyName: true }))}
                        placeholder="Search for a location..."
                        autoFocus
                        context={placeSearch}
                    />
                    {locationSecondaryText && (
                        <LocationSecondaryText>{locationSecondaryText}</LocationSecondaryText>
                    )}
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="accommodation-name">Name</FormLabel>
                    <FormInput
                        id="accommodation-name"
                        data-testid={TestIds.accommodation.nameField}
                        type="text"
                        value={draft.name}
                        onChange={e => setDraft(d => setName(d, e.target.value))}
                        placeholder="e.g. Marriott Downtown"
                        maxLength={75}
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>Dates</FormLabel>
                    <DateInputRow>
                        <DateInputGroup>
                            <CompactFormLabel htmlFor="checkin-date">Check-in</CompactFormLabel>
                            <DateTimePicker
                                id="checkin-date"
                                testId={TestIds.accommodation.checkInField}
                                value={draft.checkInDate}
                                onChange={next => setDraft(d => setCheckIn(d, next))}
                                placeholder="Select check-in"
                            />
                        </DateInputGroup>
                        <DateInputGroup>
                            <CompactFormLabel htmlFor="checkout-date">Check-out</CompactFormLabel>
                            <DateTimePicker
                                id="checkout-date"
                                testId={TestIds.accommodation.checkOutField}
                                value={draft.checkOutDate}
                                onChange={next => setDraft(d => setCheckOut(d, next))}
                                placeholder="Select check-out"
                            />
                        </DateInputGroup>
                    </DateInputRow>
                    {validation.dateError && (
                        <GuestSummary>
                            <GuestError>{validation.dateError}</GuestError>
                        </GuestSummary>
                    )}
                </FormGroup>

                <FormGroup>
                    <FormLabel>Guests</FormLabel>
                    <FormDescription>Select who is staying at this accommodation</FormDescription>
                    <GuestsList>
                        {sortedMembers.map(member => {
                            const isSelected = draft.guestIds.has(member.id)
                            return (
                                <GuestRow
                                    key={member.id}
                                    $isSelected={isSelected}
                                    onClick={() => setDraft(d => toggleGuest(d, member.id))}
                                >
                                    <GuestCheckbox $checked={isSelected} />
                                    <Avatar user={member} size="md" />
                                    <GuestName>{fullMemberName(member, user?.id)}</GuestName>
                                </GuestRow>
                            )
                        })}
                    </GuestsList>
                    <GuestSummary>
                        {validation.guestError ? (
                            <GuestError>{validation.guestError}</GuestError>
                        ) : (
                            validation.guestSummary
                        )}
                    </GuestSummary>
                </FormGroup>

                <FormGroup>
                    <FormLabel>Links</FormLabel>
                    <FormDescription>Add booking confirmations or relevant URLs (max {MAX_LINKS})</FormDescription>
                    <LinksSection>
                        {draft.links.map((link, index) => (
                            <LinkRow key={index}>
                                <LinkInput
                                    type="text"
                                    value={link}
                                    onChange={e => setDraft(d => ({ ...d, links: setLinkRow(d.links, index, e.target.value) }))}
                                    placeholder="e.g. https://booking.com/..."
                                />
                                <RemoveLinkButton
                                    type="button"
                                    onClick={() => setDraft(d => ({ ...d, links: removeLinkRow(d.links, index) }))}
                                >
                                    ✕
                                </RemoveLinkButton>
                            </LinkRow>
                        ))}
                        <AddLinkButton
                            type="button"
                            onClick={() => setDraft(d => ({ ...d, links: addLinkRow(d.links) }))}
                            disabled={draft.links.length >= MAX_LINKS}
                        >
                            <PlusIcon sx={{ fontSize: iconSizes.sm }} />
                            Add Link
                        </AddLinkButton>
                    </LinksSection>
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="accommodation-notes">Notes</FormLabel>
                    <FormDescription>Add any relevant notes about the accommodation</FormDescription>
                    <FormTextArea
                        id="accommodation-notes"
                        value={draft.notes}
                        onChange={e => setDraft(d => setNotes(d, e.target.value))}
                        placeholder="e.g. Check-in code is 1234, parking is in garage B..."
                    />
                </FormGroup>

                <PendingAttachmentPicker files={pendingFiles} onChange={setPendingFiles} />
            </form>
        </Modal>
    )
}

export default AddAccommodationModal
